import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  FileText,
  Factory,
  ShieldCheck,
  Ship,
  Radar,
  FolderClosed,
  Flag,
  Bell,
  CheckCircle2,
} from "lucide-react";
import { Reveal, Overline, StatusBadge } from "./primitives";

const Panel = ({ icon: Icon, title, badge, children, className = "" }) => (
  <div
    className={`flex flex-col rounded-xl border border-slate-100 bg-white p-4 shadow-card-base ${className}`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-demax-soft text-demax">
          <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
        </span>
        <span className="text-[12px] font-semibold text-ink">{title}</span>
      </div>
      {badge}
    </div>
    <div className="mt-3 flex-1">{children}</div>
  </div>
);

const Row = ({ label, value, tone }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-[11px] text-slateink">{label}</span>
    <span className={`text-[11px] font-semibold ${tone || "text-ink"}`}>{value}</span>
  </div>
);

export const WorkspaceExperience = () => {
  const reduce = useReducedMotion();
  return (
    <section
      id="workspace"
      data-testid="workspace-section"
      className="border-t border-slate-100 bg-slate-50/40 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Overline>Workspace Experience</Overline>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-ink md:text-4xl">
            Every module, one unified Workspace.
          </h2>
          <p className="mt-4 text-base text-slateink md:text-lg">
            A dedicated Workspace for a single full container order — interconnected, transparent
            and fully under your control.
          </p>
        </Reveal>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 rounded-2xl border border-slate-200 bg-white p-4 shadow-floating sm:p-6"
        >
          <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-4">
            <Panel icon={FileText} title="Purchase Order" badge={<StatusBadge tone="success">Confirmed</StatusBadge>}>
              <Row label="PO Number" value="#FC-20481" />
              <Row label="Value" value="$52,800" />
              <Row label="Incoterm" value="FOB İzmir" />
            </Panel>

            <Panel icon={Factory} title="Production Status" badge={<StatusBadge tone="info">Active</StatusBadge>}>
              <div className="mt-1 flex items-center justify-between text-[11px] font-medium text-slateink">
                <span>Manufacturing</span>
                <span className="text-ink">68%</span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full w-[68%] rounded-full bg-demax" />
              </div>
              <Row label="Est. completion" value="12 Jan" tone="text-slateink" />
            </Panel>

            <Panel icon={ShieldCheck} title="Inspection Status" badge={<StatusBadge tone="warning">Scheduled</StatusBadge>}>
              <Row label="Type" value="Pre-shipment" />
              <Row label="Date" value="14 Jan" />
              <Row label="Inspector" value="Assigned" tone="text-[#059669]" />
            </Panel>

            <Panel icon={Ship} title="FreightIQ™" badge={<StatusBadge tone="info">Quoting</StatusBadge>}>
              <Row label="Route" value="İzmir → Rotterdam" />
              <Row label="Best rate" value="$1,940" tone="text-demax" />
              <Row label="Transit" value="~11 days" />
            </Panel>

            <Panel
              icon={Radar}
              title="Live Shipment Tracking™"
              className="md:col-span-2"
              badge={<StatusBadge tone="warning">Preparing</StatusBadge>}
            >
              <div className="flex items-center justify-between">
                {["Booked", "Origin", "At Sea", "Arrival"].map((m, i) => (
                  <div key={m} className="flex flex-1 flex-col items-center">
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ${
                        i === 0 ? "bg-demax text-white" : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="mt-1 text-[10px] font-medium text-slateink">{m}</span>
                  </div>
                ))}
              </div>
              <div className="relative mt-2 h-1 w-full rounded-full bg-slate-100">
                <div className="absolute left-0 top-0 h-full w-[12%] rounded-full bg-demax" />
              </div>
            </Panel>

            <Panel icon={FolderClosed} title="Document Hub" badge={<StatusBadge tone="neutral">6 files</StatusBadge>}>
              <Row label="Proforma Invoice" value="Signed" tone="text-[#059669]" />
              <Row label="Packing List" value="Ready" tone="text-[#059669]" />
              <Row label="B/L Draft" value="Pending" tone="text-[#D97706]" />
            </Panel>

            <Panel icon={Flag} title="Milestones" className="md:col-span-2">
              <div className="space-y-2">
                {[
                  { t: "Purchase order confirmed", done: true },
                  { t: "Production started", done: true },
                  { t: "Inspection scheduled", done: false },
                ].map((m) => (
                  <div key={m.t} className="flex items-center gap-2">
                    <CheckCircle2
                      className={`h-3.5 w-3.5 ${m.done ? "text-[#059669]" : "text-slate-300"}`}
                    />
                    <span className={`text-[11px] ${m.done ? "text-ink" : "text-slateink"}`}>
                      {m.t}
                    </span>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel icon={Bell} title="Alerts" className="md:col-span-2" badge={<StatusBadge tone="warning">2 new</StatusBadge>}>
              <div className="space-y-2">
                <div className="rounded-md border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-[11px] font-medium text-[#92400E]">
                  Inspection window opens in 3 days — confirm scheduling.
                </div>
                <div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] font-medium text-slateink">
                  New freight quote available from FreightIQ™.
                </div>
              </div>
            </Panel>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
